import { Injectable, computed, inject, signal } from '@angular/core'
import { Router } from '@angular/router'
import axios from 'axios'
import { jwtDecode } from 'jwt-decode'
import { environment } from '../../environments/environment'
import { TokenService } from '../services/token.service'
import { auth } from './permission-checker'
import { AuthGroup, AuthUtil, PiaUser, authGroupMapping } from './auth.types'

interface PiaAccessTokenPayload {
  email?: string
  name?: string
  preferred_username?: string
  resource_access?: {
    [key: string]: { roles?: string[] } | undefined
  }
}

interface PiaTokenResponse {
  access_token: string
  refresh_token?: string
  id_token?: string
  expires_in?: number
  token_type?: string
}

const PIA_USER_STORAGE_KEY = 'piaUser'
const PIA_REFRESH_TOKEN_KEY = 'piaRefreshToken'
const PIA_ID_TOKEN_KEY = 'piaIdToken'
const PKCE_VERIFIER_KEY = 'piaPkceVerifier'
const PKCE_STATE_KEY = 'piaPkceState'

const UNRESERVED_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~'

function randomString(length: number): string {
  const bytes = new Uint8Array(length)
  crypto.getRandomValues(bytes)
  let out = ''
  for (let i = 0; i < length; i++) {
    out += UNRESERVED_CHARS[bytes[i] % UNRESERVED_CHARS.length]
  }
  return out
}

function base64UrlEncode(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

async function sha256(input: string): Promise<ArrayBuffer> {
  const data = new TextEncoder().encode(input)
  return crypto.subtle.digest('SHA-256', data)
}

@Injectable({ providedIn: 'root' })
export class PiaAuthService {
  private router = inject(Router)
  private tokenService = inject(TokenService)

  private userSignal = signal<PiaUser | undefined>(this.loadUserFromStorage())

  readonly user = this.userSignal.asReadonly()
  readonly permissions = computed<AuthUtil>(() => auth(this.userSignal()))

  private refreshInFlight: Promise<boolean> | null = null

  async login(): Promise<void> {
    const codeVerifier = randomString(64)
    const state = randomString(32)
    const codeChallenge = base64UrlEncode(await sha256(codeVerifier))

    sessionStorage.setItem(PKCE_VERIFIER_KEY, codeVerifier)
    sessionStorage.setItem(PKCE_STATE_KEY, state)

    const params = new URLSearchParams({
      client_id: environment.pia.clientId,
      scope: environment.pia.scope,
      response_type: 'code',
      redirect_uri: this.redirectUri(),
      state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    })
    window.location.href = `${environment.pia.authority}/protocol/openid-connect/auth?${params.toString()}`
  }

  async handleCallback(search: string): Promise<void> {
    const params = new URLSearchParams(search)
    const code = params.get('code')
    const returnedState = params.get('state')
    const error = params.get('error')

    if (error || !code) {
      console.error('PIA callback error:', error || 'missing code')
      void this.router.navigateByUrl('/login')
      return
    }

    const expectedState = sessionStorage.getItem(PKCE_STATE_KEY)
    const codeVerifier = sessionStorage.getItem(PKCE_VERIFIER_KEY)
    sessionStorage.removeItem(PKCE_STATE_KEY)
    sessionStorage.removeItem(PKCE_VERIFIER_KEY)

    if (!expectedState || expectedState !== returnedState || !codeVerifier) {
      console.error('PIA callback: state/verifier mismatch')
      void this.router.navigateByUrl('/login')
      return
    }

    try {
      const { data } = await axios.post<PiaTokenResponse>(
        `${environment.awsEndpointUrl.baseURL}/account-services/auth/exchange`,
        {
          code,
          redirect_uri: this.redirectUri(),
          code_verifier: codeVerifier,
        }
      )
      if (!data?.access_token) {
        throw new Error('access_token missing in exchange response')
      }
      const piaUser = this.buildUserFromToken(data.access_token)
      this.persistSession(data.access_token, data.refresh_token, data.id_token, piaUser)
      void this.router.navigateByUrl('/home')
    } catch (e) {
      const ax = e as { response?: { status?: number; data?: unknown }; message?: string }
      console.error('PIA token exchange failed', {
        status: ax?.response?.status,
        data: ax?.response?.data,
        message: ax?.message,
      })
      void this.router.navigateByUrl('/login')
    }
  }

  async refresh(): Promise<boolean> {
    if (this.refreshInFlight) return this.refreshInFlight
    this.refreshInFlight = this.performRefresh().finally(() => {
      this.refreshInFlight = null
    })
    return this.refreshInFlight
  }

  private async performRefresh(): Promise<boolean> {
    const refreshToken = localStorage.getItem(PIA_REFRESH_TOKEN_KEY)
    if (!refreshToken) return false

    try {
      const { data } = await axios.post<PiaTokenResponse>(
        `${environment.awsEndpointUrl.baseURL}/account-services/auth/refresh`,
        { refresh_token: refreshToken }
      )
      if (!data?.access_token) return false

      const piaUser = this.buildUserFromToken(data.access_token)
      this.persistSession(
        data.access_token,
        data.refresh_token ?? refreshToken,
        data.id_token ?? localStorage.getItem(PIA_ID_TOKEN_KEY) ?? undefined,
        piaUser
      )
      return true
    } catch (e) {
      const ax = e as { response?: { status?: number }; message?: string }
      console.error('PIA token refresh failed', {
        status: ax?.response?.status,
        message: ax?.message,
      })
      return false
    }
  }

  logout(): void {
    const idToken = localStorage.getItem(PIA_ID_TOKEN_KEY)
    this.clearSession()
    const params = new URLSearchParams({ client_id: environment.pia.clientId })
    if (idToken) params.set('id_token_hint', idToken)
    window.location.href = `${environment.pia.authority}/protocol/openid-connect/logout?${params.toString()}`
  }

  private redirectUri(): string {
    return `${window.location.origin}${environment.pia.redirectPath}`
  }

  private buildUserFromToken(accessToken: string): PiaUser {
    let payload: PiaAccessTokenPayload = {}
    try {
      payload = jwtDecode<PiaAccessTokenPayload>(accessToken)
    } catch {
      payload = {}
    }
    const piaRoles = payload.resource_access?.[environment.pia.clientId]?.roles ?? []
    const groups = piaRoles
      .map((role) => authGroupMapping[role])
      .filter((group): group is AuthGroup => Boolean(group))

    return {
      name: payload.name ?? payload.preferred_username,
      email: payload.email,
      groups,
      userLoggedInWithPia: true,
    }
  }

  private persistSession(
    accessToken: string,
    refreshToken: string | undefined,
    idToken: string | undefined,
    user: PiaUser
  ): void {
    if (!this.tokenService.storeToken(accessToken)) {
      throw new Error('PIA login: could not store token (browser storage quota exceeded)')
    }
    localStorage.setItem(PIA_USER_STORAGE_KEY, JSON.stringify(user))
    if (refreshToken) localStorage.setItem(PIA_REFRESH_TOKEN_KEY, refreshToken)
    if (idToken) localStorage.setItem(PIA_ID_TOKEN_KEY, idToken)
    this.userSignal.set(user)
  }

  private clearSession(): void {
    this.tokenService.removeToken()
    localStorage.removeItem(PIA_USER_STORAGE_KEY)
    localStorage.removeItem(PIA_REFRESH_TOKEN_KEY)
    localStorage.removeItem(PIA_ID_TOKEN_KEY)
    this.userSignal.set(undefined)
  }

  private loadUserFromStorage(): PiaUser | undefined {
    try {
      const raw = localStorage.getItem(PIA_USER_STORAGE_KEY)
      return raw ? (JSON.parse(raw) as PiaUser) : undefined
    } catch {
      return undefined
    }
  }
}
